/**
 * Complétude d'un espace de travail — « qu'est-ce qu'il me reste à faire ? ».
 *
 * Troisième étage d'un moule déjà éprouvé deux fois dans le projet :
 *   · `pages/syndic/building-setup.util.ts`            → un immeuble
 *   · `…/property-detail-overview/property-setup.util.ts` → un bien
 *   · celui-ci                                          → l'espace de travail
 *
 * Même forme de sortie (`score` / `doneCount` / `total` / `items`), même
 * discipline : PUR — ni Angular, ni Firestore, ni store. Le composant lit les
 * stores, en fait un instantané plat (`WorkspaceSetupInput`) et le passe ici.
 * Tout est donc testable unitairement sans TestBed.
 *
 * Trois règles portent l'essentiel du sens :
 *
 * 1. PONDÉRATION — un item vaut 3 / 2 / 1 point selon qu'il est obligatoire,
 *    important ou utile. Sans ça, « ajouter une photo de profil » pèserait
 *    autant que « créer un bail ».
 *
 * 2. APPLICABILITÉ — un item hors sujet n'est ni affiché NI compté au
 *    dénominateur (DESIGN.md §13, « le produit ne punit pas »). Un espace sans
 *    aucun bien ne se voit pas reprocher l'absence de bail : la question ne se
 *    pose pas encore. Même mécanique que `computePropertySetup`, qui saute
 *    `composition` hors résidentiel.
 *
 * 3. NIVEAU — un item vise l'ESPACE (une identité, un IBAN, un carnet de
 *    prestataires : un seul pour tout le monde) ou UN BIEN (sa fiche, son PEB,
 *    son bail, son état des lieux). Seul le second niveau se démultiplie.
 *    C'est la correction de #240 : compter les baux au niveau de l'espace
 *    faisait passer trois biens pour finis dès qu'un SEUL portait un bail. Un
 *    item porte donc `propertyUID` quand il vise un bien précis, et la sortie
 *    fournit `propertyGroups` — un bloc par bien, avec son propre score.
 */

import { ScoreLevel, scoreLevel } from '@shared/utils/score-bands';

/** Importance d'un item — pilote la pondération ET la pastille affichée. */
export type SetupLevel = 'required' | 'important' | 'useful';

/** Regroupement d'items ; devient une pastille de section dans la carte de score. */
export type SetupFamily = 'account' | 'properties' | 'renting' | 'finance';

/** Poids d'un item selon son importance. */
export const SETUP_LEVEL_POINTS: Readonly<Record<SetupLevel, number>> = {
  required: 3,
  important: 2,
  useful: 1
};

/** Ordre d'affichage des familles (du plus fondateur au plus accessoire). */
export const SETUP_FAMILY_ORDER: readonly SetupFamily[] = [
  'account',
  'properties',
  'renting',
  'finance'
];

/**
 * Nombre maximum de BIENS dépliés dans la page. Au-delà, les suivants sont
 * repliés et leur nombre est ANNONCÉ à l'écran — jamais tronqué en silence.
 * Ils continuent de compter dans le score : ils existent, ils sont juste
 * ailleurs.
 *
 * Le plafond porte sur les biens et non plus sur les items depuis #240 :
 * replier « le 6ᵉ item par bien » coupait un bien en deux, sans dire lequel.
 */
export const PROPERTY_GROUP_CAP = 3;

/** Seuil à partir duquel la fiche d'un bien est jugée complète (bande « good »). */
export const LISTING_QUALITY_TARGET = 70;

export interface WorkspaceSetupAction {
  kind: 'navigate';
  /** Route absolue, prête pour `router.navigateByUrl`. */
  route: string;
}

export interface WorkspaceSetupItem {
  /** Identifiant stable (les items « par bien » suffixent l'UID du bien). */
  id: string;
  family: SetupFamily;
  level: SetupLevel;
  titleKey: string;
  descriptionKey: string;
  /** Paramètres d'interpolation du titre (nom du bien, etc.). */
  titleParams?: Record<string, unknown>;
  /** Ligature Material. */
  icon: string;
  done: boolean;
  action: WorkspaceSetupAction;
  /** Renseigné quand l'item vise UN bien ; absent pour un item d'espace. */
  propertyUID?: string;
  /**
   * La saisie déjà enregistrée, à montrer une fois l'item `done` — pour que
   * « Modifier » ouvre sur ce qu'on a vraiment mis, pas sur un formulaire vide
   * en apparence (#480). Absent pour les items sans valeur simple à afficher.
   */
  value?: string;
}

export interface WorkspaceSetupSection {
  family: SetupFamily;
  labelKey: string;
  score: number;
  maxScore: number;
  percentage: number;
  doneCount: number;
  total: number;
}

/** Un bien et tout ce qui s'y rattache — le second étage de la page. */
export interface PropertySetupGroup {
  propertyUID: string;
  /** Nom affiché, déjà replié sur l'UID par l'appelant si le bien n'en porte pas. */
  name: string;
  score: number;
  maxScore: number;
  /** 0-100, arrondi — la barre du bloc. */
  percentage: number;
  doneCount: number;
  total: number;
  /** Tous les items de ce bien, faits comme restants, dans l'ordre. */
  items: WorkspaceSetupItem[];
}

export interface WorkspaceSetup {
  /** Points obtenus. */
  score: number;
  /** Points possibles (items applicables uniquement). */
  maxScore: number;
  /** 0-100, arrondi. */
  percentage: number;
  doneCount: number;
  total: number;
  level: ScoreLevel;
  /** Tout ce qui est affichable, à plat : items d'espace + biens dépliés. */
  items: WorkspaceSetupItem[];
  /** Les items qui ne visent aucun bien en particulier. */
  workspaceItems: WorkspaceSetupItem[];
  /** Un bloc par bien AYANT ENCORE À FAIRE, pire d'abord, plafonné. */
  propertyGroups: PropertySetupGroup[];
  /** Combien de biens en retard ont été repliés (0 si aucun). */
  hiddenPropertyCount: number;
  /**
   * Vrai quand l'espace ne contient aucun bien. La page bascule alors sur un
   * état vide plein écran : tout le reste attend ce premier bien, autant ne
   * proposer qu'un seul chemin plutôt qu'une carte noyée dans sept autres.
   */
  needsFirstProperty: boolean;
  sections: WorkspaceSetupSection[];
}

/** Instantané d'un bien, réduit à ce dont la complétude a besoin. */
export interface PropertySetupSnapshot {
  propertyUID: string;
  /** Nom affiché ; peut être vide, l'appelant fournit alors un repli. */
  name: string;
  /** Score de qualité d'annonce, 0-100 (`PropertyScoringService`). */
  listingPercentage: number;
  isResidential: boolean;
  hasActivePeb: boolean;
  /** Locataires rattachés à CE bien. */
  tenantCount: number;
  /** Baux portant sur CE bien. */
  contractCount: number;
  /** États des lieux portant sur CE bien. */
  inventoryCount: number;
}

export interface WorkspaceSetupUser {
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  photoURL?: string | null;
}

export interface WorkspaceSetupWorkspace {
  name?: string | null;
  vatNumber?: string | null;
  billingStreet?: string | null;
  billingCity?: string | null;
}

export interface WorkspaceSetupInput {
  user: WorkspaceSetupUser | null;
  emailVerified: boolean;
  workspace: WorkspaceSetupWorkspace | null;
  /** Un workspace professionnel doit renseigner un numéro de TVA. */
  isProfessionalWorkspace: boolean;
  properties: readonly PropertySetupSnapshot[];
  providerCount: number;
  hasDocuments: boolean;
  /** Au moins un profil de facturation porte un IBAN exploitable. */
  hasBankAccount: boolean;
}

const I18N = 'gettingStarted.items.';

const FAMILY_LABEL_KEYS: Readonly<Record<SetupFamily, string>> = {
  account: 'gettingStarted.families.account',
  properties: 'gettingStarted.families.properties',
  renting: 'gettingStarted.families.renting',
  finance: 'gettingStarted.families.finance'
};

/** Une chaîne « renseignée » = non vide une fois les espaces retirés. */
function filled(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Fabrique un item. `keyBase` porte les clés i18n ; `id` peut en différer pour
 * les items « par bien », qui suffixent l'UID afin de rester uniques
 * (clé de `track` côté template, et point d'ancrage des specs).
 */
function item(
  keyBase: string,
  family: SetupFamily,
  level: SetupLevel,
  icon: string,
  done: boolean,
  route: string,
  extras?: {
    id?: string;
    titleParams?: Record<string, unknown>;
    propertyUID?: string;
    value?: string;
  }
): WorkspaceSetupItem {
  return {
    id: extras?.id ?? keyBase,
    family,
    level,
    icon,
    done,
    titleKey: `${I18N}${keyBase}.title`,
    descriptionKey: `${I18N}${keyBase}.description`,
    ...(extras?.titleParams ? { titleParams: extras.titleParams } : {}),
    ...(extras?.propertyUID ? { propertyUID: extras.propertyUID } : {}),
    ...(extras?.value ? { value: extras.value } : {}),
    action: { kind: 'navigate', route }
  };
}

/**
 * Les items qui ne visent aucun bien : le compte, l'espace, et ce qui est
 * mutualisé entre tous les biens (un IBAN, un carnet de prestataires).
 */
function buildWorkspaceItems(input: WorkspaceSetupInput): WorkspaceSetupItem[] {
  const items: WorkspaceSetupItem[] = [];
  const user = input.user;
  const workspace = input.workspace;
  const hasProperties = input.properties.length > 0;

  // ── Compte & espace de travail ───────────────────────────────────────────
  const identity = [user?.firstName, user?.lastName]
    .filter(part => filled(part))
    .join(' ')
    .trim();
  const billingAddress = [workspace?.billingStreet, workspace?.billingCity]
    .filter(part => filled(part))
    .join(', ');

  items.push(
    item(
      'profileIdentity',
      'account',
      'required',
      'badge',
      filled(user?.firstName) && filled(user?.lastName),
      '/apps/user-profile/profile',
      { value: identity }
    ),
    item(
      'emailVerified',
      'account',
      'required',
      'mark_email_read',
      input.emailVerified,
      '/verify-required'
    ),
    item(
      'profilePhone',
      'account',
      'important',
      'phone',
      filled(user?.phoneNumber),
      '/apps/user-profile/profile',
      { value: user?.phoneNumber ?? undefined }
    ),
    item(
      'profilePhoto',
      'account',
      'useful',
      'account_circle',
      filled(user?.photoURL),
      '/apps/user-profile/profile'
    ),
    item(
      'workspaceName',
      'account',
      'required',
      'workspaces',
      filled(workspace?.name),
      '/apps/workspace/settings/info',
      { value: workspace?.name ?? undefined }
    ),
    item(
      'billingAddress',
      'account',
      'important',
      'location_on',
      filled(workspace?.billingStreet) && filled(workspace?.billingCity),
      '/apps/workspace/settings/billing',
      { value: billingAddress }
    )
  );

  // La TVA ne concerne qu'un espace professionnel — sinon la question ne se pose pas.
  if (input.isProfessionalWorkspace) {
    items.push(
      item(
        'vatNumber',
        'account',
        'important',
        'receipt_long',
        filled(workspace?.vatNumber),
        '/apps/workspace/settings/billing',
        { value: workspace?.vatNumber ?? undefined }
      )
    );
  }

  // ── Le premier bien ──────────────────────────────────────────────────────
  items.push(
    item('firstProperty', 'properties', 'required', 'home_work', hasProperties, '/properties/add')
  );

  // ── Finances & tiers ─────────────────────────────────────────────────────
  // Volontairement mutualisés : un seul IBAN sert tous les biens, les
  // démultiplier réclamerait N fois la même chose.
  if (hasProperties) {
    items.push(
      item(
        'bankAccount',
        'finance',
        'required',
        'account_balance',
        input.hasBankAccount,
        '/apps/workspace/settings/profiles'
      ),
      item('documents', 'finance', 'important', 'folder', input.hasDocuments, '/apps/documents'),
      item(
        'serviceProvider',
        'finance',
        'useful',
        'engineering',
        input.providerCount > 0,
        '/service-providers'
      )
    );
  }

  return items;
}

/**
 * Les items d'UN bien.
 *
 * Contrairement aux items d'espace, ils sont poussés qu'ils soient faits ou
 * non : le bloc du bien affiche sa propre progression, et un bien irréprochable
 * dont tous les items auraient été omis afficherait « 0/0 ». Seule
 * l'applicabilité les retire (PEB hors résidentiel, état des lieux sans bail).
 */
function buildPropertyItems(property: PropertySetupSnapshot): WorkspaceSetupItem[] {
  const uid = property.propertyUID;
  const name = property.name;

  const items: WorkspaceSetupItem[] = [
    item(
      'propertyListing',
      'properties',
      'important',
      'fact_check',
      property.listingPercentage >= LISTING_QUALITY_TARGET,
      `/properties/${uid}/getting-started`,
      {
        id: `propertyListing:${uid}`,
        propertyUID: uid,
        titleParams: { name, percentage: property.listingPercentage }
      }
    )
  ];

  // Le PEB est une obligation légale belge sur le résidentiel uniquement.
  if (property.isResidential) {
    items.push(
      item(
        'propertyPeb',
        'properties',
        'required',
        'energy_savings_leaf',
        property.hasActivePeb,
        `/properties/${uid}/certificates`,
        { id: `propertyPeb:${uid}`, propertyUID: uid, titleParams: { name } }
      )
    );
  }

  items.push(
    item(
      'propertyTenant',
      'renting',
      'important',
      'person_add',
      property.tenantCount > 0,
      '/tenants/add',
      { id: `propertyTenant:${uid}`, propertyUID: uid, titleParams: { name } }
    ),
    item(
      'propertyContract',
      'renting',
      'required',
      'description',
      property.contractCount > 0,
      '/contracts/add',
      { id: `propertyContract:${uid}`, propertyUID: uid, titleParams: { name } }
    )
  );

  // Un état des lieux n'a de sens qu'attaché à un bail de CE bien.
  if (property.contractCount > 0) {
    items.push(
      item(
        'propertyInventory',
        'renting',
        'useful',
        'checklist',
        property.inventoryCount > 0,
        `/properties/${uid}/contracts`,
        { id: `propertyInventory:${uid}`, propertyUID: uid, titleParams: { name } }
      )
    );
  }

  return items;
}

function summarise(items: readonly WorkspaceSetupItem[]): {
  score: number;
  maxScore: number;
  percentage: number;
  doneCount: number;
  total: number;
} {
  let score = 0;
  let maxScore = 0;
  let doneCount = 0;

  for (const it of items) {
    const points = SETUP_LEVEL_POINTS[it.level];
    maxScore += points;
    if (it.done) {
      score += points;
      doneCount++;
    }
  }

  return {
    score,
    maxScore,
    percentage: maxScore === 0 ? 0 : Math.round((score / maxScore) * 100),
    doneCount,
    total: items.length
  };
}

/**
 * Calcule la complétude d'un espace de travail.
 *
 * Le score porte sur TOUS les items applicables, y compris ceux des biens
 * repliés ; seul l'AFFICHAGE plafonne le nombre de biens dépliés
 * (`hiddenPropertyCount` dit combien, pour que la page l'annonce).
 */
export function computeWorkspaceSetup(input: WorkspaceSetupInput): WorkspaceSetup {
  const workspaceItems = buildWorkspaceItems(input);

  const allGroups: PropertySetupGroup[] = input.properties.map(property => {
    const items = buildPropertyItems(property);
    return {
      propertyUID: property.propertyUID,
      name: property.name,
      items,
      ...summarise(items)
    };
  });

  // Un bien intégralement complété sort de la liste : il n'a plus rien à
  // demander, et il n'est PAS compté comme « replié » — rien ne lui est caché
  // (DESIGN.md §13). Pire d'abord parmi ceux qui restent : c'est le bien le
  // plus en retard qui mérite l'écran. À égalité, l'ordre alphabétique, pour
  // que la page ne se réordonne pas d'un rendu à l'autre.
  const groupsWithWork = allGroups
    .filter(group => group.doneCount < group.total)
    .sort((a, b) => a.percentage - b.percentage || a.name.localeCompare(b.name));

  const propertyGroups = groupsWithWork.slice(0, PROPERTY_GROUP_CAP);
  const hiddenPropertyCount = groupsWithWork.length - propertyGroups.length;

  const allItems = [...workspaceItems, ...allGroups.flatMap(group => group.items)];
  const overall = summarise(allItems);

  // Une famille dont aucun item n'est applicable (« Location » sur un espace
  // sans bien) est retirée : afficher une pastille à 0 % pour une question qui
  // ne se pose pas encore serait exactement le reproche gratuit que §13 interdit.
  const sections: WorkspaceSetupSection[] = SETUP_FAMILY_ORDER
    .map(family => {
      const rollup = summarise(allItems.filter(it => it.family === family));
      return {
        family,
        labelKey: FAMILY_LABEL_KEYS[family],
        score: rollup.score,
        maxScore: rollup.maxScore,
        percentage: rollup.percentage,
        doneCount: rollup.doneCount,
        total: rollup.total
      };
    })
    .filter(section => section.total > 0);

  return {
    ...overall,
    level: scoreLevel(overall.percentage),
    items: [...workspaceItems, ...propertyGroups.flatMap(group => group.items)],
    workspaceItems,
    propertyGroups,
    hiddenPropertyCount,
    needsFirstProperty: input.properties.length === 0,
    sections
  };
}
